import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Page,
  Popup,
  Segmented,
} from "framework7-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DeleteButton from "../../components/delete-button";
import PopupNavbar from "../../components/popup-navbar";
import { usePopupBackClose } from "../../hooks/use-back-close";
import { useEnterKeys } from "../../hooks/use-enter-keys";
import { vibrate } from "../../hooks/use-haptics";
import { useOnline } from "../../hooks/use-online";
import { useToast } from "../../hooks/use-toast";
import { selectGroupTags, selectMe } from "../../redux/reducer/app-slice";
import {
  deleteRecurring,
  saveRecurring,
  setRecurringPaused,
} from "../../redux/reducer/recurring";
import { countLabel } from "../../utils/format";
import { confirmDialog } from "../../utils/confirm-dialog";
import { SHARE_MODES, resplitForTotal } from "../../utils/money";
import { RECURRING_INTERVALS, berlinDateKey } from "../../utils/recurring";
import {
  INTERVAL_LABELS,
  RECURRING_TEXTS,
  describeSchedule,
  initialRecurringValues,
  needsShortMonthHint,
  startDateLimits,
  startsInPast,
  toRecurringPayload,
  validateRecurringForm,
} from "../../utils/recurring-form";
import MoneyInput from "../money-input/money-input";
import NoteInput, { NOTE_INPUT_NAME } from "../note-input";
import PersonPickerPopup from "../person-picker-popup";
import ReceiversPopup, { MODE_LABELS, isEveryone } from "../receivers-popup";
import TagPicker from "../tags/tag-picker";
import "./recurring.css";

const TITLE_INPUT = "recurring-title";
const START_INPUT = "recurring-start";
const END_INPUT = "recurring-end";

const receiversLabel = ({ mode, receivers }, participants) => {
  if (receivers.length === 0) return "Empfänger auswählen";
  const people = isEveryone(receivers, participants)
    ? `Alle (${participants.length})`
    : countLabel(receivers.length, "Person", "Personen");
  return mode === SHARE_MODES.weights || mode === SHARE_MODES.percent
    ? `${people} · ${MODE_LABELS[mode]}`
    : people;
};

const scheduleHints = (values, today, isNew) => {
  const schedule = { interval: values.interval, start_date: values.startDate };
  return [
    describeSchedule(schedule),
    needsShortMonthHint(schedule) && RECURRING_TEXTS.shortMonths,
    startsInPast(values, today, isNew) && RECURRING_TEXTS.backfill,
  ]
    .filter(Boolean)
    .join(" ");
};

const RecurringFormPopup = ({ groupId, participants, template, opened, onClose, onSaved }) => {
  const me = useSelector(selectMe);
  const groupTags = useSelector(selectGroupTags);
  const dispatch = useDispatch();
  const online = useOnline();
  const { showError } = useToast();
  const today = berlinDateKey(Date.now());
  const isNew = !template?.id;
  const [values, setValues] = useState(() => initialRecurringValues(template, participants, me, today));
  const [busy, setBusy] = useState(null);
  const [payerOpened, setPayerOpened] = useState(false);
  const [receiversOpened, setReceiversOpened] = useState(false);
  const popupRef = useRef(null);
  const backClose = usePopupBackClose(opened, () => busy === null);
  useEnterKeys(backClose.id, {
    [TITLE_INPUT]: { hint: "next", next: "amount" },
    amount: { hint: "done" },
    [START_INPUT]: { hint: "done" },
    [END_INPUT]: { hint: "done" },
    [NOTE_INPUT_NAME]: { hint: "enter" },
  });

  const latest = useRef({});
  latest.current = { participants, me, today };
  useEffect(() => {
    if (!opened) return;
    const current = latest.current;
    setValues(initialRecurringValues(template, current.participants, current.me, current.today));
    setBusy(null);
  }, [opened, template]);

  const set = (field) => (value) => setValues((current) => ({ ...current, [field]: value }));
  const setSplit = (update) =>
    setValues((current) => ({
      ...current,
      split: typeof update === "function" ? update(current.split) : update,
    }));
  const changeAmount = (cents) =>
    setValues((current) => ({
      ...current,
      amount: cents,
      split: resplitForTotal(cents, current.split),
    }));

  const payer = participants.find((p) => values.payer !== "" && `${p.id}` === `${values.payer}`);
  const closePopup = () => popupRef.current?.close();

  const finish = (list) => {
    vibrate();
    onSaved(list);
    closePopup();
  };
  const fail = (message) => {
    setBusy(null);
    showError(message);
  };

  const save = () => {
    if (busy || !online) return;
    const invalid = validateRecurringForm(values, { participants, isNew, today });
    if (invalid) {
      showError(invalid);
      return;
    }
    setBusy("save");
    dispatch(saveRecurring(groupId, template?.id ?? null, toRecurringPayload(values), finish, fail));
  };

  const togglePaused = () => {
    if (busy || !online || isNew) return;
    setBusy("pause");
    dispatch(setRecurringPaused(groupId, template.id, !template.paused, finish, fail));
  };

  const remove = () => {
    if (busy || !online || isNew) return;
    confirmDialog({
      title: `„${template.title}“ löschen?`,
      text: "Sie wird künftig nicht mehr eingetragen. Bereits eingetragene Ausgaben bleiben erhalten.",
      confirmText: "Löschen",
      destructive: true,
      cssClass: "delete-recurring-dialog",
      onConfirm: () => {
        setBusy("delete");
        dispatch(deleteRecurring(groupId, template.id, finish, fail));
      },
    });
  };

  const limits = startDateLimits(today, isNew);
  const disabled = busy !== null || !online;

  return (
    <Popup
      {...backClose}
      className="recurring-form-popup"
      opened={opened}
      onPopupOpened={(popup) => {
        popupRef.current = popup;
      }}
      onPopupClosed={() => {
        setPayerOpened(false);
        setReceiversOpened(false);
        onClose();
      }}
    >
      <Page>
        <PopupNavbar title={isNew ? "Wiederkehrend anlegen" : "Wiederkehrende Ausgabe"} />
        {!online && (
          <Block key="offline" className="recurring-offline">
            {RECURRING_TEXTS.offline}
          </Block>
        )}
        <Block>
          <List strongIos insetIos outlineIos>
            <ListInput
              name={TITLE_INPUT}
              outline
              type="text"
              label="Titel"
              floatingLabel
              autocomplete="off"
              autocapitalize="sentences"
              value={values.title}
              onChange={(event) => set("title")(event.target.value)}
            />
            <MoneyInput value={values.amount} onChange={changeAmount} />
          </List>

          <TagPicker groupTags={groupTags} selected={values.tags} onChange={set("tags")} />

          <List strong outline inset dividers>
            <ListItem title="Von" link onClick={() => setPayerOpened(true)}>
              {payer?.name || "Zahler auswählen"}
            </ListItem>
            <ListItem title="Für" link onClick={() => setReceiversOpened(true)}>
              {receiversLabel(values.split, participants)}
            </ListItem>
          </List>

          <BlockTitle className="recurring-interval-title">Wiederholen</BlockTitle>
          <Block className="recurring-interval-block">
            <Segmented strong className="recurring-interval" role="group" aria-label="Wiederholen">
              {Object.values(RECURRING_INTERVALS).map((interval) => (
                <Button
                  key={interval}
                  small
                  active={values.interval === interval}
                  className={`recurring-interval-${interval}`}
                  onClick={() => set("interval")(interval)}
                >
                  {INTERVAL_LABELS[interval]}
                </Button>
              ))}
            </Segmented>
          </Block>

          <List strongIos insetIos outlineIos className="recurring-dates">
            <ListInput
              name={START_INPUT}
              className="recurring-start"
              outline
              type="date"
              label="Beginn"
              min={limits.min}
              max={limits.max}
              value={values.startDate}
              onChange={(event) => set("startDate")(event.target.value)}
            />
            <ListInput
              name={END_INPUT}
              className="recurring-end"
              outline
              type="date"
              label="Ende (optional)"
              min={values.startDate || undefined}
              value={values.endDate}
              onChange={(event) => set("endDate")(event.target.value)}
            />
            <NoteInput value={values.note} onChange={set("note")} />
          </List>
          <BlockFooter className="recurring-schedule-hint">
            {scheduleHints(values, today, isNew)}
          </BlockFooter>
        </Block>

        <Block inset className="recurring-actions">
          <Button fill disabled={disabled} onClick={save}>
            Speichern
          </Button>
          {!isNew && (
            <Button
              key="pause"
              tonal
              className="recurring-pause-button"
              disabled={disabled}
              onClick={togglePaused}
            >
              {template.paused ? "Fortsetzen" : "Pausieren"}
            </Button>
          )}
          {!isNew && (
            <DeleteButton
              key="delete"
              className="recurring-delete-button"
              busy={busy === "delete"}
              disabled={disabled}
              onClick={remove}
            >
              Löschen
            </DeleteButton>
          )}
        </Block>

        {/* Popup rule: one always-rendered wrapper per nested popup – see AGENTS.md. */}
        <div className="recurring-payer-popup-wrapper">
          <PersonPickerPopup
            className="recurring-payer-popup"
            title="Zahler auswählen"
            opened={payerOpened}
            onClose={() => setPayerOpened(false)}
            participants={participants}
            selectedId={payer?.id}
            onSelect={(id) => {
              set("payer")(id);
              setPayerOpened(false);
            }}
          />
        </div>
        <div className="recurring-receivers-popup-wrapper">
          <ReceiversPopup
            opened={receiversOpened}
            onClose={() => setReceiversOpened(false)}
            amount={values.amount}
            split={values.split}
            participants={participants}
            updateSplit={setSplit}
          />
        </div>
      </Page>
    </Popup>
  );
};

export default RecurringFormPopup;
